/**
 * Channel Presets — Stackable, reusable behaviors for channeled attacks
 * 
 * Presets follow the same pattern as ImpactPresets:
 * - Each preset is a factory function that returns a ChannelPreset
 * - Presets can be layered on top of each other
 * - Presets communicate via shared `state` object
 */

import { Color3, Color4, Vector3 } from '@babylonjs/core';
import { logger } from '../../core/Logger';
import type { 
  ChannelPreset, 
  ChannelHitDetectionConfig,
  DamageCurveFunction,
  DamageCurveType 
} from '../../types/combat';

// ============================================================================
// STATIC FX CONFIGURATION
// ============================================================================

/**
 * Configuration for a static FX mesh attached to a bone during channel.
 * These are visual-only (no physics, no collision, no movement).
 */
export interface StaticFxConfig {
  /** Unique ID for this static FX (used for tracking/cleanup) */
  id?: string;
  
  /** Embedded entity reference (e.g., '#beam') */
  assetPath: string;
  
  /** Bone to attach the FX to (defaults to preset's boneName) */
  boneName?: string;
  
  /** Scale multiplier (default 1.0) */
  scale?: number;
  
  /** Local offset from bone position */
  offset?: Vector3;
  
  /** 
   * Orientation mode for the FX:
   * - 'bone': Inherit bone rotation (default, rotates with hand/arm)
   * - 'world': Use world axes (stays horizontal regardless of bone rotation)  
   * - 'facing': Align to character's facing direction (horizontal beam forward)
   */
  orientationMode?: 'bone' | 'world' | 'facing';
  
  /**
   * Forward direction in local or world space (depending on orientationMode).
   * For 'facing' mode, this is relative to character facing (default: forward = Z+)
   * For 'world' mode, this is the world-space forward direction.
   */
  forward?: Vector3;
  
  /**
   * Up direction in local or world space.
   * Default: Vector3.Up() (Y+)
   */
  up?: Vector3;
  
  /**
   * Additional local rotation in degrees (applied after orientation).
   * Useful for fine-tuning asset orientation.
   */
  localRotation?: { x?: number; y?: number; z?: number };
  
  /** Start showing FX at this normalized time (0-1), default 0 */
  startTime?: number;
  
  /** Stop showing FX at this normalized time, default 1 (end) */
  endTime?: number;
  
  /** Video playback speed multiplier (default 1.0). Creates separate video instance per FX. */
  playbackRate?: number;
  
  /**
   * Video loop range - the portion of the video to loop (normalized 0-1).
   * Requires per-instance video texture (not shared).
   * Example: { start: 0.2, end: 0.8 } loops the middle 60% of the video
   */
  videoLoopRange?: { start: number; end: number };
  
  /**
   * When to spawn this FX relative to attack phases:
   * - 'channel': Spawn when channel begins (default, after windup)
   * - 'windup': Spawn immediately when attack starts (during windup animation)
   */
  spawnPhase?: 'channel' | 'windup';
}

// ============================================================================
// DAMAGE CURVE FUNCTIONS
// ============================================================================

export const DamageCurves: Record<DamageCurveType, (params?: Record<string, number>) => DamageCurveFunction> = {
  constant: () => () => 1.0,
  
  rampUp: (params) => {
    const maxMult = params?.maxMultiplier ?? 2.0;
    return (t: number) => 1.0 + (maxMult - 1.0) * t;
  },
  
  rampDown: (params) => {
    const maxMult = params?.maxMultiplier ?? 2.0;
    return (t: number) => maxMult - (maxMult - 1.0) * t;
  },
  
  pulse: (params) => {
    const freq = params?.pulseFrequency ?? 2.0;
    return (t: number) => 0.5 + 0.5 * Math.sin(t * freq * Math.PI * 2);
  },
  
  burst: (params) => {
    const burstMult = params?.burstMultiplier ?? 3.0;
    return (t: number) => t < 0.1 ? burstMult : 1.0;
  },
};

// ============================================================================
// PARTICLE PRESETS
// ============================================================================

export interface ParticleStreamConfig {
  /** Particle preset name */
  preset: string;
  /** Bone to emit from */
  boneName: string;
  /** Direction mode: 'facing' follows character, 'bone' follows bone forward */
  directionMode?: 'facing' | 'bone';
  /** Scale multiplier */
  scale?: number;
  /** Color override */
  color?: Color4;
}

/**
 * Creates a preset that emits continuous particles during channel
 */
export function createParticleStreamPreset(config: ParticleStreamConfig): ChannelPreset {
  return {
    id: `particle_stream_${config.preset}`,
    name: `Particle Stream (${config.preset})`,
    
    onStart: (ctx) => {
      const emissionId = ctx.actions.startParticles({
        preset: config.preset,
        position: ctx.actions.getBonePosition(config.boneName) ?? undefined,
        direction: config.directionMode === 'bone' 
          ? ctx.actions.getBoneForward(config.boneName) ?? undefined
          : ctx.actions.getFacing(),
        scale: config.scale,
        color: config.color,
      });
      
      // Store emission ID in shared state for cleanup
      ctx.state.particleEmissionId = emissionId;
    },
    
    onTick: (ctx) => {
      const emissionId = ctx.state.particleEmissionId as string | undefined;
      if (!emissionId) return;
      
      // Update direction based on mode
      const direction = config.directionMode === 'bone'
        ? ctx.actions.getBoneForward(config.boneName)
        : ctx.actions.getFacing();
        
      if (direction) {
        ctx.actions.updateParticleDirection(emissionId, direction);
      }
    },
    
    onEnd: (ctx) => {
      const emissionId = ctx.state.particleEmissionId as string | undefined;
      if (emissionId) {
        ctx.actions.stopParticles(emissionId);
        delete ctx.state.particleEmissionId;
      }
    },
  };
}

// ============================================================================
// MUZZLE LIGHT PRESETS
// ============================================================================

export interface MuzzleLightColorEntry {
  /** Light color */
  color: Color3;
  /** Relative probability weight (will be normalized) */
  probability: number;
  /** Optional intensity override for this color */
  intensity?: number;
  /** Optional range override for this color */
  range?: number;
}

export interface MuzzleLightConfig {
  /** Bone to attach light to */
  boneName: string;
  /** Single light color (default white) - ignored if colors array is provided */
  color?: Color3;
  /** Light intensity (default 2.0) */
  intensity?: number;
  /** Light range in units (default 5.0) */
  range?: number;
  /** Offset from bone: +Z is forward, +Y is up, +X is right */
  offset?: Vector3;
  
  // === Flashing/Pulsing ===
  /** Array of colors with probabilities for random flashing */
  colors?: MuzzleLightColorEntry[];
  /** Color switch interval range in seconds [min, max] */
  switchInterval?: [number, number];
}

/**
 * Pick a random color from weighted entries
 */
function pickRandomColor(entries: MuzzleLightColorEntry[]): MuzzleLightColorEntry {
  // Normalize probabilities
  const totalWeight = entries.reduce((sum, e) => sum + e.probability, 0);
  const roll = Math.random() * totalWeight;
  
  let cumulative = 0;
  for (const entry of entries) {
    cumulative += entry.probability;
    if (roll <= cumulative) {
      return entry;
    }
  }
  return entries[entries.length - 1];
}

/**
 * Get random interval within range
 */
function randomInterval(range: [number, number]): number {
  return range[0] + Math.random() * (range[1] - range[0]);
}

/**
 * Creates a preset that adds a light at the muzzle bone during the channel.
 * The light follows the bone position and is cleaned up on end.
 * Supports flashing colors with configurable interval and probability weights.
 */
export function createMuzzleLightPreset(config: MuzzleLightConfig): ChannelPreset {
  const hasFlashing = config.colors && config.colors.length > 0 && config.switchInterval;
  
  return {
    id: `muzzle_light_${config.boneName}`,
    name: `Muzzle Light (${config.boneName})`,
    
    onStart: (ctx) => {
      // Pick initial color
      let initialColor = config.color;
      let initialIntensity = config.intensity ?? 2.0;
      let initialRange = config.range ?? 5.0;
      
      if (hasFlashing && config.colors!.length > 0) {
        const picked = pickRandomColor(config.colors!);
        initialColor = picked.color;
        initialIntensity = picked.intensity ?? initialIntensity;
        initialRange = picked.range ?? initialRange;
      }
      
      const lightId = ctx.actions.createBoneLight({
        bone: config.boneName,
        color: initialColor,
        intensity: initialIntensity,
        range: initialRange,
        offset: config.offset,
      });
      
      ctx.state.muzzleLightId = lightId;
      ctx.state.muzzleLightNextSwitch = hasFlashing ? randomInterval(config.switchInterval!) : Infinity;
      ctx.state.muzzleLightBaseIntensity = config.intensity ?? 2.0;
      ctx.state.muzzleLightBaseRange = config.range ?? 5.0;
      
      logger.combat.debug(`MuzzleLightPreset: Created light ${lightId} at ${config.boneName}`);
    },
    
    onTick: (ctx) => {
      const lightId = ctx.state.muzzleLightId as string | undefined;
      if (!lightId) return;
      
      // Update light position to follow bone
      ctx.actions.updateBoneLightPosition(lightId, config.boneName, config.offset);
      
      // Handle color flashing
      if (hasFlashing && config.colors!.length > 0) {
        const nextSwitch = ctx.state.muzzleLightNextSwitch as number;
        
        if (ctx.channelDuration >= nextSwitch) {
          // Time to switch color
          const picked = pickRandomColor(config.colors!);
          const baseIntensity = ctx.state.muzzleLightBaseIntensity as number;
          const newIntensity = picked.intensity ?? baseIntensity;
          
          ctx.actions.updateBoneLightColor(lightId, picked.color, newIntensity);
          
          // Schedule next switch
          ctx.state.muzzleLightNextSwitch = ctx.channelDuration + randomInterval(config.switchInterval!);
        }
      }
    },
    
    onEnd: (ctx) => {
      const lightId = ctx.state.muzzleLightId as string | undefined;
      if (lightId) {
        ctx.actions.destroyLight(lightId);
        delete ctx.state.muzzleLightId;
        delete ctx.state.muzzleLightNextSwitch;
        delete ctx.state.muzzleLightBaseIntensity;
        delete ctx.state.muzzleLightBaseRange;
        logger.combat.debug(`MuzzleLightPreset: Destroyed light ${lightId}`);
      }
    },
  };
}

// ============================================================================
// HIT DETECTION PRESETS
// ============================================================================

export interface BeamHitConfig {
  /** Hit detection shape */
  type: 'cone' | 'cylinder' | 'sphere';
  /** Range in meters */
  range: number;
  /** Width: cone half-angle (degrees) or cylinder/sphere radius */
  width: number;
  /** Base damage per tick */
  baseDamage: number;
  /** Minimum seconds between damage to same target */
  damageInterval?: number;
  /** Damage curve type or custom function */
  damageCurve?: DamageCurveType | DamageCurveFunction;
  /** Curve parameters */
  curveParams?: Record<string, number>;
}

/**
 * Creates a preset that performs hit detection and applies damage
 */
export function createBeamDamagePreset(config: BeamHitConfig): ChannelPreset {
  const getCurve = (): DamageCurveFunction => {
    if (!config.damageCurve) return () => 1.0;
    if (typeof config.damageCurve === 'function') return config.damageCurve;
    return DamageCurves[config.damageCurve](config.curveParams);
  };
  
  const curve = getCurve();
  
  return {
    id: `beam_damage_${config.type}`,
    name: `Beam Damage (${config.type})`,
    
    onTick: (ctx) => {
      const hitConfig: ChannelHitDetectionConfig = {
        type: config.type,
        range: config.range,
        width: config.width,
        damage: config.baseDamage,
        damageInterval: config.damageInterval ?? 0.25,
      };
      
      const targets = ctx.actions.detectTargets(hitConfig);
      const damageMultiplier = curve(ctx.normalizedTime);
      const damage = config.baseDamage * damageMultiplier;
      
      for (const target of targets) {
        if (target.canDamage) {
          ctx.actions.applyDamage(target.targetId, damage);
        }
      }
      
      // Track hits in state for other presets to use
      ctx.state.lastHits = targets;
      ctx.state.lastDamage = damage;
    },
  };
}

// ============================================================================
// EFFECT PRESETS
// ============================================================================

export interface ChannelEffectConfig {
  /** Screen impact preset on channel start */
  startImpact?: 'small' | 'medium' | 'large';
  /** Particle burst on start */
  startBurst?: { preset: string; scale?: number };
  /** Screen impact on channel end */
  endImpact?: 'small' | 'medium' | 'large';
  /** Particle burst on end */
  endBurst?: { preset: string; scale?: number };
}

/**
 * Creates a preset for visual/audio effects at channel start/end
 */
export function createChannelEffectsPreset(config: ChannelEffectConfig): ChannelPreset {
  return {
    id: 'channel_effects',
    name: 'Channel Effects',
    
    onStart: (ctx) => {
      if (config.startImpact) {
        ctx.actions.triggerImpact(config.startImpact, 0.5);
      }
      if (config.startBurst) {
        ctx.actions.burstParticles(config.startBurst.preset, {
          scale: config.startBurst.scale,
          position: ctx.actions.getPosition(),
        });
      }
    },
    
    onEnd: (ctx) => {
      if (config.endImpact) {
        ctx.actions.triggerImpact(config.endImpact, 0.7);
      }
      if (config.endBurst) {
        ctx.actions.burstParticles(config.endBurst.preset, {
          scale: config.endBurst.scale,
          position: ctx.actions.getPosition(),
        });
      }
    },
  };
}

// ============================================================================
// THEME PRESETS — Pre-configured visual themes
// ============================================================================

export interface MagicBeamTheme {
  particlePreset: string;
  color?: Color4;
  startBurstPreset?: string;
  endBurstPreset?: string;
}

export const MagicBeamThemes: Record<string, MagicBeamTheme> = {
  ice: {
    particlePreset: 'magic_beam_ice',
    color: new Color4(0.6, 0.8, 1.0, 1.0),
    startBurstPreset: 'ice_burst',
    endBurstPreset: 'ice_shatter',
  },
  fire: {
    particlePreset: 'magic_beam_fire',
    color: new Color4(1.0, 0.5, 0.2, 1.0),
    startBurstPreset: 'fire_burst',
    endBurstPreset: 'fire_explosion',
  },
  lightning: {
    particlePreset: 'magic_beam_lightning',
    color: new Color4(0.8, 0.8, 1.0, 1.0),
    startBurstPreset: 'spark_burst',
    endBurstPreset: 'lightning_strike',
  },
  arcane: {
    particlePreset: 'magic_beam_arcane',
    color: new Color4(0.8, 0.4, 1.0, 1.0),
    startBurstPreset: 'arcane_burst',
    endBurstPreset: 'arcane_explosion',
  },
};

/**
 * Creates a complete magic beam attack configuration from a theme
 */
export interface MagicBeamConfig {
  theme: keyof typeof MagicBeamThemes | MagicBeamTheme;
  boneName: string;
  range: number;
  width: number;
  baseDamage: number;
  damageInterval?: number;
  damageCurve?: DamageCurveType;
  directionMode?: 'facing' | 'bone';
}

export function createMagicBeamPresets(config: MagicBeamConfig): ChannelPreset[] {
  const theme = typeof config.theme === 'string' 
    ? MagicBeamThemes[config.theme] 
    : config.theme;
  
  if (!theme) {
    throw new Error(`Unknown magic beam theme: ${config.theme}`);
  }
  
  const presets: ChannelPreset[] = [];
  
  // 1. Particle stream
  presets.push(createParticleStreamPreset({
    preset: theme.particlePreset,
    boneName: config.boneName,
    directionMode: config.directionMode ?? 'facing',
    color: theme.color,
  }));
  
  // 2. Hit detection and damage
  presets.push(createBeamDamagePreset({
    type: 'cone',
    range: config.range,
    width: config.width,
    baseDamage: config.baseDamage,
    damageInterval: config.damageInterval,
    damageCurve: config.damageCurve ?? 'constant',
  }));
  
  // 3. Visual effects
  if (theme.startBurstPreset || theme.endBurstPreset) {
    presets.push(createChannelEffectsPreset({
      startBurst: theme.startBurstPreset ? { preset: theme.startBurstPreset } : undefined,
      endBurst: theme.endBurstPreset ? { preset: theme.endBurstPreset } : undefined,
    }));
  }
  
  return presets;
}

// ============================================================================
// BEAM WITH PROJECTILES — Continuous particles + projectile spawning
// ============================================================================

export interface BeamWithProjectilesConfig {
  /** Particle preset for continuous emission */
  particlePreset: string;
  
  /** Bone to emit particles/projectiles from */
  boneName: string;
  
  /** Start emitting particles at this normalized time (0-1), default 0 */
  particleStartTime?: number;
  
  /** Stop emitting particles at this normalized time, default 1 (end) */
  particleEndTime?: number;
  
  /** Projectile spawn rate (projectiles per second), 0 = no projectiles */
  projectileRate?: number;
  
  /** Start spawning projectiles at this normalized time (0-1), default 0 */
  projectileStartTime?: number;
  
  /** Stop spawning projectiles at this normalized time, default 1 (end) */
  projectileEndTime?: number;
  
  /** 
   * Static FX meshes to spawn and attach to bone during the beam.
   * These are visual-only (no collision, no movement).
   * Each entry is an embedded entity reference (e.g., '#beam').
   */
  staticFx?: StaticFxConfig[];
  
  /** Particle burst preset on channel start */
  startBurstPreset?: string;
  
  /** Particle burst preset on channel end */
  endBurstPreset?: string;
  
  /** Optional particle scale */
  particleScale?: number;
  
  /** Optional color override */
  color?: Color4;
  
  /** Direction mode: 'facing' follows character, 'bone' follows bone */
  directionMode?: 'facing' | 'bone';
  
  /** Particle jitter settings */
  jitter?: {
    position?: number;
    direction?: number;
  };
}

/**
 * Creates a preset that emits continuous particles AND spawns projectiles during channel.
 * 
 * Example usage:
 * ```typescript
 * createBeamWithProjectilesPreset({
 *   particlePreset: 'magic_beam_ice',
 *   boneName: 'mixamorig:RightHand',
 *   projectileRate: 5, // 5 projectiles per second
 *   particleStartTime: 0.1, // Start particles at 10% into animation
 *   projectileStartTime: 0.2, // Start projectiles at 20%
 *   projectileEndTime: 0.9, // Stop at 90%
 * })
 * ```
 */
export function createBeamWithProjectilesPreset(config: BeamWithProjectilesConfig): ChannelPreset {
  const particleStartTime = config.particleStartTime ?? 0;
  const particleEndTime = config.particleEndTime ?? 1;
  const projectileStartTime = config.projectileStartTime ?? 0;
  const projectileEndTime = config.projectileEndTime ?? 1;
  const projectileInterval = config.projectileRate ? 1 / config.projectileRate : 0;
  
  // Convert staticFx to the format expected by ChannelPreset.staticFxConfig
  const staticFxConfig = config.staticFx?.map(fx => ({
    id: fx.id,
    assetPath: fx.assetPath,
    boneName: fx.boneName ?? config.boneName,
    scale: fx.scale,
    offset: fx.offset ? { x: fx.offset.x, y: fx.offset.y, z: fx.offset.z } : undefined,
    orientationMode: fx.orientationMode,
    forward: fx.forward ? { x: fx.forward.x, y: fx.forward.y, z: fx.forward.z } : undefined,
    up: fx.up ? { x: fx.up.x, y: fx.up.y, z: fx.up.z } : undefined,
    localRotation: fx.localRotation,
    startTime: fx.startTime,
    endTime: fx.endTime,
    playbackRate: fx.playbackRate,
    videoLoopRange: fx.videoLoopRange,
    spawnPhase: fx.spawnPhase,
  }));
  
  return {
    id: `beam_projectiles_${config.particlePreset}`,
    name: `Beam with Projectiles (${config.particlePreset})`,
    
    // Expose static FX config so CombatSystem can spawn windup-phase FX
    staticFxConfig,
    
    onStart: (ctx) => {
      // Initialize state
      ctx.state.particleActive = false;
      ctx.state.projectileActive = false;
      ctx.state.lastProjectileTime = 0;
      ctx.state.projectilesSpawned = 0;
      ctx.state.activeStaticFxIds = []; // Track spawned static FX
      
      // Request static FX spawn for any that start at time 0 and are not windup-phase
      // (windup-phase FX are spawned by CombatSystem before channel starts)
      if (config.staticFx && config.staticFx.length > 0) {
        const fxToSpawn = config.staticFx.filter(fx => 
          (fx.startTime ?? 0) <= 0 && (fx.spawnPhase ?? 'channel') === 'channel'
        );
        if (fxToSpawn.length > 0) {
          ctx.state.spawnStaticFxRequest = fxToSpawn.map(fx => ({
            id: fx.id,
            assetPath: fx.assetPath,
            boneName: fx.boneName ?? config.boneName,
            scale: fx.scale ?? 1.0,
            offset: fx.offset,
            orientationMode: fx.orientationMode,
            forward: fx.forward,
            up: fx.up,
            localRotation: fx.localRotation,
            playbackRate: fx.playbackRate,
            videoLoopRange: fx.videoLoopRange,
          }));
        }
      }
      
      // Start burst effect
      if (config.startBurstPreset) {
        const pos = ctx.actions.getBonePosition(config.boneName) ?? ctx.actions.getPosition();
        ctx.actions.burstParticles(config.startBurstPreset, { position: pos, scale: config.particleScale });
      }
      
      logger.combat.debug(`BeamPreset: Channel started for ${ctx.attackerId}`);
    },
    
    onTick: (ctx) => {
      const t = ctx.normalizedTime;
      const now = ctx.channelDuration;
      
      // === Static FX Timing ===
      // Check for static FX that should spawn/despawn based on normalized time
      if (config.staticFx && config.staticFx.length > 0) {
        const activeIds = (ctx.state.activeStaticFxIds as string[]) ?? [];
        
        for (let i = 0; i < config.staticFx.length; i++) {
          const fx = config.staticFx[i];
          const fxId = `staticFx_${i}`;
          const startTime = fx.startTime ?? 0;
          const endTime = fx.endTime ?? 1;
          const isActive = activeIds.includes(fxId);
          const shouldBeActive = t >= startTime && t <= endTime;
          
          if (shouldBeActive && !isActive) {
            // Request spawn
            const requests = (ctx.state.spawnStaticFxRequest as any[]) ?? [];
            requests.push({
              id: fxId,
              assetPath: fx.assetPath,
              boneName: fx.boneName ?? config.boneName,
              scale: fx.scale ?? 1.0,
              offset: fx.offset,
              orientationMode: fx.orientationMode,
              forward: fx.forward,
              up: fx.up,
              localRotation: fx.localRotation,
              playbackRate: fx.playbackRate,
              videoLoopRange: fx.videoLoopRange,
            });
            ctx.state.spawnStaticFxRequest = requests;
            activeIds.push(fxId);
            ctx.state.activeStaticFxIds = activeIds;
          } else if (!shouldBeActive && isActive) {
            // Request despawn
            const requests = (ctx.state.despawnStaticFxRequest as string[]) ?? [];
            requests.push(fxId);
            ctx.state.despawnStaticFxRequest = requests;
            ctx.state.activeStaticFxIds = activeIds.filter(id => id !== fxId);
          }
        }
      }
      
      // === Particle Emission ===
      const shouldEmitParticles = t >= particleStartTime && t <= particleEndTime;
      
      if (shouldEmitParticles) {
        ctx.state.particleActive = true;
        
        // Emit particles at bone position
        ctx.actions.spawnParticlesAtBone({
          preset: config.particlePreset,
          bone: config.boneName,
          scale: config.particleScale,
          direction: config.directionMode ?? 'facing',
          jitter: config.jitter,
          count: 1,
        });
      } else if (ctx.state.particleActive && t > particleEndTime) {
        ctx.state.particleActive = false;
      }
      
      // === Projectile Spawning ===
      const shouldSpawnProjectiles = t >= projectileStartTime && t <= projectileEndTime && projectileInterval > 0;
      
      if (shouldSpawnProjectiles) {
        ctx.state.projectileActive = true;
        
        const timeSinceLastProjectile = now - (ctx.state.lastProjectileTime as number);
        
        if (timeSinceLastProjectile >= projectileInterval) {
          // Get spawn position and direction
          const position = ctx.actions.getBonePosition(config.boneName) ?? ctx.actions.getPosition();
          const direction = config.directionMode === 'bone'
            ? (ctx.actions.getBoneForward(config.boneName) ?? ctx.actions.getFacing())
            : ctx.actions.getFacing();
          
          // Request projectile spawn via state - CombatSystem will handle it
          ctx.state.spawnProjectileRequest = {
            position: position.clone(),
            direction: direction.clone(),
            spawnIndex: ctx.state.projectilesSpawned as number,
          };
          
          ctx.state.lastProjectileTime = now;
          ctx.state.projectilesSpawned = (ctx.state.projectilesSpawned as number) + 1;
          
          logger.combat.debug(`BeamPreset: Projectile spawn requested #${ctx.state.projectilesSpawned} at t=${t.toFixed(2)}`);
        }
      } else if (ctx.state.projectileActive && t > projectileEndTime) {
        ctx.state.projectileActive = false;
      }
    },
    
    onEnd: (ctx) => {
      // End burst effect
      if (config.endBurstPreset) {
        const pos = ctx.actions.getBonePosition(config.boneName) ?? ctx.actions.getPosition();
        ctx.actions.burstParticles(config.endBurstPreset, { position: pos, scale: config.particleScale });
      }
      
      logger.combat.debug(`BeamPreset: Channel ended: ${ctx.reason}, spawned ${ctx.state.projectilesSpawned} projectiles`);
      
      // Cleanup state
      ctx.state.particleActive = false;
      ctx.state.projectileActive = false;
    },
  };
}

// ============================================================================
// UTILITY: Combine presets
// ============================================================================

/**
 * Combines multiple presets into a single array, filtering duplicates by ID
 */
export function combinePresets(...presetArrays: (ChannelPreset | ChannelPreset[])[]): ChannelPreset[] {
  const seen = new Set<string>();
  const result: ChannelPreset[] = [];
  
  for (const item of presetArrays) {
    const arr = Array.isArray(item) ? item : [item];
    for (const preset of arr) {
      if (!seen.has(preset.id)) {
        seen.add(preset.id);
        result.push(preset);
      }
    }
  }
  
  return result;
}

/**
 * Helper to layer additional behavior onto existing presets
 */
export function withPreset(
  base: ChannelPreset[],
  additional: ChannelPreset | ChannelPreset[]
): ChannelPreset[] {
  return combinePresets(base, additional);
}
